import {useTranslation} from 'react-i18next';
import {getListClassNames} from 'styles';
import React from 'react';
import {
	DetailsRowCompat as DetailsRow,
	IDetailsRowBaseProps,
	IDetailsRowProps,
	useTheme,
} from 'components/FluentUICompat';
import {LoadWrapper} from 'components/LoadWrapper';
import {WorkbenchHeader} from '../Headers';
import {EntityList, EntityListColumn} from 'components/EntityList';
import {DashboardTooltipTranslationProvider} from '../DashboardTooltipTranslationProvider';
import {useApproverTileColumns} from '../../hooks/useTileColumns';
import {TileContainer} from '../../hooks/useTileContainer';
import {RequirementStatus} from 'types';
import {DashboardCardProps} from '..';
import {sortObjectsByDate} from 'helpers/sortHelpers';
import {useDashboardQueries} from 'features/Dashboard/hooks/useDashboardQueries';
import {createHrefFromRequirement} from 'features/Requirements/requirementLinks.utils';
import {useVkoApproverWorkbenchStyles} from '../VkoApproverWorkbench/useVkoApproverWorkbenchStyles';

const RequirementsList = ({items, approverOnly}: any) => {
	const approverTileColumns: EntityListColumn[] =
		useApproverTileColumns(approverOnly);
	return (
		<DashboardTooltipTranslationProvider>
			<EntityList
				sticky={false}
				selectionMode={0}
				items={items}
				columns={approverTileColumns}
				onRenderRow={(props?: IDetailsRowProps) => (
					<a href={createHrefFromRequirement(props?.item)}>
						<DetailsRow {...(props as IDetailsRowProps)} />
					</a>
				)}
			/>
		</DashboardTooltipTranslationProvider>
	);
};

export const MyReviewItems: React.FC<
	DashboardCardProps & {enforce3columns?: boolean}
> = ({
	halfHeight = false,
	itemCountLoaded,
	isVkoApproverDashboard,
	enforce3columns,
}) => {
	const {t} = useTranslation('features/dashboard', {
		keyPrefix: 'ApproverOverview',
	});

	const {getMyRequirementsReviewQuery} = useDashboardQueries();
	const {data, loading} = getMyRequirementsReviewQuery();
	const theme = useTheme();
	const classNames = getListClassNames(theme, halfHeight);
	const {classNames: vkoApproverClassNames} = useVkoApproverWorkbenchStyles();

	const openReviews = React.useMemo(() => {
		return sortObjectsByDate(
			data?.myRequirementsReview?.filter(
				r => r.status === RequirementStatus.Approval,
			) ?? [],
			'approvalWorkFlow.sentToApprovalTimestamp',
			false,
		);
	}, [data]);

	const completedReviews = React.useMemo(() => {
		return sortObjectsByDate(
			data?.myRequirementsReview?.filter(
				r =>
					r.status === RequirementStatus.Final ||
					r.status === RequirementStatus.Approval,
			) ?? [],
			'approvalWorkFlow.clearingTimestamp',
			false,
		);
	}, [data]);

	React.useEffect(() => {
		if (!loading && data?.myRequirementsReview)
			itemCountLoaded?.(
				data?.myRequirementsReview?.filter(
					r =>
						r.status === RequirementStatus.Final ||
						r.status === RequirementStatus.Approval,
				).length,
			);
	}, [itemCountLoaded, data, loading]);

	const visibleTiles = React.useMemo(() => {
		const tiles = [];
		if (loading) {
			tiles.push(<LoadWrapper loading={true} />);
			return tiles;
		}
		if (enforce3columns === true || openReviews.length > 0) {
			tiles.push(
				<div
					className={
						isVkoApproverDashboard === true
							? vkoApproverClassNames.fixedActivityContainer
							: classNames.activityContainer
					}
					key='openReviews'
				>
					<LoadWrapper loading={loading}>
						<WorkbenchHeader
							halfHeight
							title={t('OpenReviews')}
							items={openReviews.length}
						/>
						<div data-is-scrollable>
							<RequirementsList
								items={openReviews}
								approverOnly={!enforce3columns}
							/>
						</div>
					</LoadWrapper>
				</div>,
			);
		}
		if (enforce3columns === true || completedReviews.length > 0) {
			tiles.push(
				<div
					className={
						isVkoApproverDashboard === true
							? vkoApproverClassNames.fixedActivityContainer
							: classNames.activityContainer
					}
					key='completedReviews'
				>
					<LoadWrapper loading={loading}>
						<WorkbenchHeader halfHeight title={t('CompletedReviews')} />
						<div data-is-scrollable>
							<RequirementsList
								items={completedReviews}
								approverOnly={!enforce3columns}
							/>
						</div>
					</LoadWrapper>
				</div>,
			);
			if (enforce3columns === true) {
				tiles.push(<div></div>);
			}
		}
		return tiles;
	}, [openReviews, completedReviews, loading, t, classNames]);

	return <TileContainer visibleTiles={visibleTiles} />;
};
